/**
 * Availability + calendar-blocking hook for BookingWidget.
 *
 * Responsibilities (preserved 1:1 from the original inline logic):
 *   - Fetch /properties/{id}/availability once on property change.
 *   - Expand room_bookings date ranges into per-day blocklists.
 *   - Expose `isDateUnavailable(date)` for the CHECK-IN calendar and
 *     `isDateUnavailableForCheckout(date)` for the CHECKOUT calendar,
 *     honoring turnover-day semantics (a new booking's check-in date
 *     is a valid checkout date for the previous booking).
 *
 * DO NOT simplify the two-calendar / turnover-day logic without regression
 * testing — it is the result of multiple user-reported fixes.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { BACKEND_URL } from '../config';

const API_BASE = `${BACKEND_URL}/api`;

export default function useAvailabilityCalendar({
  property,
  checkInDate,
  bookingType,
  selectedRoom,
}) {
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [roomSpecificBlocks, setRoomSpecificBlocks] = useState({});
  const [loadingAvailability, setLoadingAvailability] = useState(true);

  const propertyId = property?._id || property?.id;

  useEffect(() => {
    if (!propertyId) {
      setLoadingAvailability(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingAvailability(true);
        const response = await axios.get(`${API_BASE}/properties/${propertyId}/availability`);
        if (cancelled) return;

        setUnavailableDates(response.data.unavailable_ranges || []);

        // Expand room_bookings date ranges into per-day strings.
        const roomBlocks = {};
        if (response.data.room_bookings) {
          for (const [roomId, bookings] of Object.entries(response.data.room_bookings)) {
            const roomKey = `room_${roomId}`;
            roomBlocks[roomKey] = [];
            for (const booking of bookings) {
              const startDate = new Date(booking.check_in);
              const endDate = new Date(booking.check_out);
              const currentDate = new Date(startDate);
              while (currentDate < endDate) {
                const dateStr = currentDate.toISOString().split('T')[0];
                if (!roomBlocks[roomKey].includes(dateStr)) {
                  roomBlocks[roomKey].push(dateStr);
                }
                currentDate.setDate(currentDate.getDate() + 1);
              }
            }
          }
        }
        setRoomSpecificBlocks(roomBlocks);
      } catch (err) {
        console.error('Error fetching availability:', err);
      } finally {
        if (!cancelled) setLoadingAvailability(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  const checkIfDateUnavailable = useCallback(
    (date, isCheckoutCalendar = false) => {
      const checkDateStr = date.toISOString().split('T')[0];

      // --- FULL PROPERTY BRANCH --- //
      if (bookingType === 'full_property' || !selectedRoom) {
        for (const range of unavailableDates) {
          let checkInStr = range.check_in;
          let checkOutStr = range.check_out;

          if (!checkInStr.includes('T')) checkInStr += 'T00:00:00Z';
          else if (!checkInStr.endsWith('Z')) checkInStr += 'Z';
          if (!checkOutStr.includes('T')) checkOutStr += 'T00:00:00Z';
          else if (!checkOutStr.endsWith('Z')) checkOutStr += 'Z';

          const rangeStart = new Date(checkInStr);
          const rangeEnd = new Date(checkOutStr);
          const checkDate = new Date(date);

          const checkDateUTC = Date.UTC(
            checkDate.getFullYear(),
            checkDate.getMonth(),
            checkDate.getDate(),
          );
          const rangeStartUTC = Date.UTC(
            rangeStart.getUTCFullYear(),
            rangeStart.getUTCMonth(),
            rangeStart.getUTCDate(),
          );
          const rangeEndUTC = Date.UTC(
            rangeEnd.getUTCFullYear(),
            rangeEnd.getUTCMonth(),
            rangeEnd.getUTCDate(),
          );

          const isBlockedDate = range.blocks_checkin_only === true;

          if (isCheckoutCalendar) {
            // Host blocks: hard-off on both sides (no turnover semantics).
            if (isBlockedDate) {
              if (checkDateUTC >= rangeStartUTC && checkDateUTC <= rangeEndUTC) return true;
              continue;
            }
            if (checkInDate) {
              const selectedCheckInUTC = Date.UTC(
                checkInDate.getFullYear(),
                checkInDate.getMonth(),
                checkInDate.getDate(),
              );
              if (rangeStartUTC > selectedCheckInUTC && checkDateUTC > rangeStartUTC) return true;
              if (checkDateUTC > rangeStartUTC && checkDateUTC < rangeEndUTC) return true;
            } else if (checkDateUTC > rangeStartUTC && checkDateUTC < rangeEndUTC) {
              return true;
            }
          } else {
            // CHECK-IN mode
            if (isBlockedDate) {
              if (checkDateUTC === rangeStartUTC) return true;
            } else if (checkDateUTC >= rangeStartUTC && checkDateUTC < rangeEndUTC) {
              return true;
            }
          }
        }

        // For entire property (CHECK-IN only), also block if any room is blocked.
        if (!isCheckoutCalendar && bookingType === 'full_property' && roomSpecificBlocks) {
          for (const key in roomSpecificBlocks) {
            if (key.startsWith('room_') && roomSpecificBlocks[key]) {
              if (roomSpecificBlocks[key].includes(checkDateStr)) return true;
            }
          }
        }
        return false;
      }

      // --- SINGLE-ROOM BRANCH --- //
      if (bookingType === 'single_room' && selectedRoom) {
        if (
          !isCheckoutCalendar &&
          roomSpecificBlocks.entire_unit &&
          roomSpecificBlocks.entire_unit.includes(checkDateStr)
        ) {
          return true;
        }

        if (!isCheckoutCalendar) {
          const roomKey = `room_${selectedRoom.room_number}`;
          if (
            roomSpecificBlocks[roomKey] &&
            roomSpecificBlocks[roomKey].includes(checkDateStr)
          ) {
            return true;
          }
        }

        for (const range of unavailableDates) {
          if (range.room_id !== undefined && range.room_id !== selectedRoom.room_number) continue;

          let checkInStr = range.check_in;
          let checkOutStr = range.check_out;
          if (!checkInStr.includes('T')) checkInStr += 'T00:00:00Z';
          else if (!checkInStr.endsWith('Z')) checkInStr += 'Z';
          if (!checkOutStr.includes('T')) checkOutStr += 'T00:00:00Z';
          else if (!checkOutStr.endsWith('Z')) checkOutStr += 'Z';

          const rangeStart = new Date(checkInStr);
          const rangeEnd = new Date(checkOutStr);
          const checkDate = new Date(date);

          const checkDateUTC = Date.UTC(
            checkDate.getFullYear(),
            checkDate.getMonth(),
            checkDate.getDate(),
          );
          const rangeStartUTC = Date.UTC(
            rangeStart.getUTCFullYear(),
            rangeStart.getUTCMonth(),
            rangeStart.getUTCDate(),
          );
          const rangeEndUTC = Date.UTC(
            rangeEnd.getUTCFullYear(),
            rangeEnd.getUTCMonth(),
            rangeEnd.getUTCDate(),
          );

          const isBlockedDate = range.blocks_checkin_only === true;

          if (isCheckoutCalendar) {
            if (isBlockedDate) {
              if (checkDateUTC >= rangeStartUTC && checkDateUTC <= rangeEndUTC) return true;
              continue;
            }
            if (checkInDate) {
              const selectedCheckInUTC = Date.UTC(
                checkInDate.getFullYear(),
                checkInDate.getMonth(),
                checkInDate.getDate(),
              );
              if (rangeStartUTC > selectedCheckInUTC && checkDateUTC > rangeStartUTC) return true;
            }
            if (checkDateUTC > rangeStartUTC && checkDateUTC < rangeEndUTC) return true;
          } else {
            if (isBlockedDate) {
              if (checkDateUTC === rangeStartUTC) return true;
            } else if (checkDateUTC >= rangeStartUTC && checkDateUTC < rangeEndUTC) {
              return true;
            }
          }
        }
        return false;
      }

      return false;
    },
    [bookingType, selectedRoom, unavailableDates, roomSpecificBlocks, checkInDate],
  );

  const isDateUnavailable = useCallback(
    (date) => checkIfDateUnavailable(date, false),
    [checkIfDateUnavailable],
  );
  const isDateUnavailableForCheckout = useCallback(
    (date) => checkIfDateUnavailable(date, true),
    [checkIfDateUnavailable],
  );

  return {
    loadingAvailability,
    isDateUnavailable,
    isDateUnavailableForCheckout,
    unavailableDates,
    roomSpecificBlocks,
  };
}
